import React, { useEffect } from "react";

import { css } from "@emotion/css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useChatContent } from "@/hooks/useChatContent.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Separator } from "@/uiKit/atoms/Separator.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { MessageCard } from "@/uiKit/molecules/chat/MessageCard/MessageCard.tsx";

import { PatientEventCard } from "./PatientEventMessage";

export type MessagesListProps = {
  patientId: string;
};

export const MessagesList: React.FC<MessagesListProps> = ({ patientId }) => {
  const styles = useStyles(makeStyle);
  const { t } = useTranslation();
  const me = Queries.user.useMe({});
  const messages = useChatContent(patientId, me.id).content;
  const hasMessages = messages && messages.length > 0;
  const readMessages = Queries.messaging.useReadMessages();

  useEffect(() => {
    readMessages.mutate({ patientId });
  }, [patientId]);
  return (
    <div className={styles.container}>
      {hasMessages &&
        messages.map(content => {
          switch (content.type) {
            case "newDay":
              return (
                <Separator
                  key={content.date.toISO()}
                  text={content.date.toLocaleString(DateTime.DATE_HUGE)}
                  classes={{
                    separator: styles.dateSeparator,
                  }}
                />
              );
            case "message":
              return <MessageCard key={content.message.id} message={content.message} />;
            case "unread":
              return (
                <Separator
                  key={content.type}
                  text={t("messaging.unread")}
                  classes={{
                    separator: styles.unreadSeparator,
                  }}
                />
              );
            case "event":
              return <PatientEventCard key={content.event.id} event={content.event} />;
          }
        })}
      {!hasMessages && (
        <div className={styles.noContent}>
          <Typography>{t("messaging.noMessages")}</Typography>
        </div>
      )}
    </div>
  );
};

const makeStyle = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "column",
    width: "100%",
  }),
  noContent: css({
    width: "100%",
    height: "50vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontStyle: "italic",
    color: theme.palette.text.disabled,
  }),
  dateSeparator: css({
    backgroundColor: "transparent",
  }),
  unreadSeparator: css({
    backgroundColor: theme.palette.text.disabled,
  }),
});
